import { persistentSettingsService } from '../services/persistentSettingsService';
import { cleanQuotes } from './global';
import { debug as logDebug } from './console';
import { normalizePerformanceTargetColor } from './performanceTargetColor';

const SESSION_STORAGE_KEY = 'performanceTarget';

export interface PerformanceTargetSessionData {
  targetName?: string;
  targetColor?: string;
}

export function readPerformanceTargetSession(): PerformanceTargetSessionData {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PerformanceTargetSessionData;
    return {
      targetName:
        typeof parsed.targetName === 'string' ? parsed.targetName : undefined,
      targetColor:
        typeof parsed.targetColor === 'string'
          ? normalizePerformanceTargetColor(parsed.targetColor)
          : undefined,
    };
  } catch {
    return {};
  }
}

export function writePerformanceTargetSession(
  partial: PerformanceTargetSessionData
): void {
  try {
    const existing = readPerformanceTargetSession();
    const next: PerformanceTargetSessionData = { ...existing, ...partial };
    if (partial.targetName !== undefined) {
      next.targetName = partial.targetName === 'No Target' ? '' : partial.targetName;
    }
    if (partial.targetColor !== undefined) {
      next.targetColor = normalizePerformanceTargetColor(partial.targetColor);
    }
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    logDebug('performanceTargetPersistence: Error writing sessionStorage', error);
  }
}

export async function loadPerformanceTargetColorFromPersistentSettings(
  userId: number,
  className: string,
  projectId: number
): Promise<string | null> {
  try {
    const settings = await persistentSettingsService.loadSettings(
      userId,
      className,
      projectId
    );
    if (
      settings?.performanceTargetColor &&
      typeof settings.performanceTargetColor === 'string'
    ) {
      const cleaned = cleanQuotes(settings.performanceTargetColor);
      logDebug('performanceTargetPersistence: Loaded target color from persistent settings', cleaned);
      return normalizePerformanceTargetColor(cleaned);
    }
  } catch (error) {
    logDebug('performanceTargetPersistence: Error loading target color from persistent settings', error);
  }
  return null;
}

export async function savePerformanceTargetToPersistentSettings(
  userId: number,
  className: string,
  projectId: number,
  partial: { targetName?: string; targetColor?: string }
): Promise<void> {
  const payload: Record<string, string> = {};
  if (partial.targetName !== undefined) {
    payload.performanceTarget =
      partial.targetName === 'No Target' ? '' : partial.targetName;
  }
  if (partial.targetColor !== undefined) {
    payload.performanceTargetColor = normalizePerformanceTargetColor(partial.targetColor);
  }
  if (Object.keys(payload).length === 0) return;

  await persistentSettingsService.saveSettings(
    userId,
    className,
    projectId,
    payload
  );
  logDebug('performanceTargetPersistence: Saved target settings to persistent storage', payload);
}
